import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { selectToolCollection, toggleMobileDrawer } from "../../../actions";
import UploadButton from "./UploadButton";
import { doesAnyLayerHavePhotoImages } from "../../../services/layers";
import "./MobileToolCarousel.css";

const MobileToolCarousel = ({
  tools,
  activeTool,
  routeId,
  design,
  location,
  selectToolCollection,
  navigate,
  toggleTools,
  toggleMobileDrawer,
  mobileDrawer
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationDirection, setAnimationDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const initializedRef = useRef(false);

  // Filter tools that should be visible
  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      if (tool.id === "photos") {
        return doesAnyLayerHavePhotoImages(design);
      }
      return true;
    });
  }, [tools, design]);

  // Initialize with the first tool (Style) on component mount
  useEffect(() => {
    // Only initialize once on first mount
    if (
      !initializedRef.current &&
      filteredTools.length > 0 &&
      !activeTool.activeTool
    ) {
      initializedRef.current = true;
      const firstTool = filteredTools[0];
      setCurrentIndex(0);
      selectToolCollection({ activeTool: firstTool.label, isVisible: true });

      // Navigate to the first tool
      if (navigate) {
        navigate(
          `/${routeId}/customize/${firstTool.id}${location.search || ""}`
        );
      }
    }
  }, [
    filteredTools,
    activeTool.activeTool,
    selectToolCollection,
    navigate,
    routeId,
    location
  ]);

  // Sync with active tool when it changes externally
  useEffect(() => {
    // Only update if we have an active tool
    if (activeTool.activeTool) {
      const activeIndex = filteredTools.findIndex(
        (tool) => tool.label === activeTool.activeTool
      );
      if (activeIndex !== -1 && activeIndex !== currentIndex) {
        setCurrentIndex(activeIndex);
      }
    }
  }, [activeTool.activeTool, filteredTools, currentIndex]);

  // Sync currentIndex with URL path
  useEffect(() => {
    if (location && location.pathname && !isAnimating) {
      const pathParts = location.pathname.split("/");
      const currentToolId = pathParts[pathParts.length - 1];

      if (currentToolId && currentToolId !== "customize") {
        const toolIndex = filteredTools.findIndex(
          (tool) => tool.id === currentToolId
        );
        if (toolIndex !== -1 && toolIndex !== currentIndex) {
          setCurrentIndex(toolIndex);
        }
      }
    }
  }, [location, filteredTools, currentIndex, isAnimating]);

  // Track the drawer state and active tool together for consistency
  useEffect(() => {
    // Ensure drawer state and tool visibility are in sync
    if (mobileDrawer && activeTool.isVisible !== mobileDrawer.isOpen) {
      // Update one to match the other (prioritize mobileDrawer.isOpen)
      selectToolCollection({
        activeTool: activeTool.activeTool,
        isVisible: mobileDrawer.isOpen
      });
    }
  }, [activeTool, mobileDrawer, selectToolCollection]);

  // Enhanced closeUploadTool function with error handling for DOM manipulation
  const closeUploadTool = useCallback(() => {
    try {
      // Update Redux state - this should be the primary mechanism
      selectToolCollection({
        activeTool: activeTool.activeTool,
        isVisible: false
      });
      toggleMobileDrawer(false);

      // As a fallback, safely use direct DOM manipulation
      try {
        const activeToolContainer = document.querySelector(
          ".tools:not(.tools-hidden)"
        );
        if (activeToolContainer) {
          activeToolContainer.classList.add("tools-hidden");
        }

        // Remove tools-active class from canvas
        const canvasContainer = document.querySelector(".canvas-container");
        if (
          canvasContainer &&
          canvasContainer.classList.contains("tools-active")
        ) {
          canvasContainer.classList.remove("tools-active");
        }
      } catch (error) {
        // Failed to manipulate DOM
      }
    } catch (error) {
      // Error handling failed silently
    }
  }, [activeTool, selectToolCollection, toggleMobileDrawer]);

  // Handle Upload tool clicks
  const handleUploadToolClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Get current tool
      const uploadTool = filteredTools.find(
        (t) => t.id === "upload" || t.id === "photos"
      );
      if (!uploadTool) return;

      // Force close drawer for upload tool using our enhanced approach
      closeUploadTool();
    },
    [filteredTools, closeUploadTool]
  );

  // If no tools after filtering, return empty div
  if (filteredTools.length === 0) {
    return <div />;
  }

  // Ensure currentIndex is within bounds
  const safeIndex = Math.min(
    Math.max(0, currentIndex),
    filteredTools.length - 1
  );
  const currentTool = filteredTools[safeIndex];
  const isActive =
    activeTool.activeTool === currentTool.label &&
    (activeTool.isVisible || mobileDrawer?.isOpen);

  // Setup double-click detection for Upload tool - moved after currentTool is defined
  useEffect(() => {
    if (
      currentTool &&
      (currentTool.id === "upload" || currentTool.id === "photos")
    ) {
      // Add a special double-click handler for the upload carousel item
      const uploadItem = document.querySelector(
        `.carousel-item[data-tool-id="${currentTool.id}"]`
      );

      if (uploadItem) {
        const handleDblClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeUploadTool();
        };

        uploadItem.addEventListener("dblclick", handleDblClick);

        return () => {
          uploadItem.removeEventListener("dblclick", handleDblClick);
        };
      }
    }
  }, [currentTool, activeTool, closeUploadTool]);

  // Add a global event listener for Upload Graphics to ensure it can be closed
  useEffect(() => {
    const handleGlobalUploadClick = (e) => {
      // Check if we clicked within the active tool drawer when Upload Graphics is active
      if (
        activeTool.activeTool &&
        (activeTool.activeTool === "Upload Graphics" ||
          activeTool.activeTool === "Upload Photos/Full Color Art") &&
        mobileDrawer?.isOpen
      ) {
        // Get the carousel item for upload tool
        const uploadItem = document.querySelector(
          `.carousel-item[data-tool-id="upload"], .carousel-item[data-tool-id="photos"]`
        );

        // If clicked on the carousel item or its children
        if (
          uploadItem &&
          (uploadItem === e.target || uploadItem.contains(e.target))
        ) {
          // Use the direct DOM method to close it
          closeUploadTool();
        }
      }
    };

    // Add global event listener
    document.addEventListener("click", handleGlobalUploadClick);

    // Clean up
    return () => {
      document.removeEventListener("click", handleGlobalUploadClick);
    };
  }, [activeTool, mobileDrawer, closeUploadTool]);

  // Special fix for Upload Graphics
  useEffect(() => {
    // Check if Upload Graphics is active and open
    if (
      (activeTool.activeTool === "Upload Graphics" ||
        activeTool.activeTool === "Upload Photos/Full Color Art") &&
      (mobileDrawer?.isOpen || false)
    ) {
      // Add a special class to the body for Upload Graphics detection
      document.body.classList.add("upload-tool-active");

      // Add direct click handler to help close the drawer
      const closeUploadOnOutsideClick = (e) => {
        // Skip if we clicked inside a .tools element or our mobile carousel
        const isInTools = e.target.closest(".tools");
        const isInCarousel = e.target.closest(".mobile-tool-carousel");

        if (!isInTools && !isInCarousel) {
          closeUploadTool();
          document.removeEventListener("click", closeUploadOnOutsideClick);
        }
      };

      // Add the handler
      document.addEventListener("click", closeUploadOnOutsideClick);

      return () => {
        document.body.classList.remove("upload-tool-active");
        document.removeEventListener("click", closeUploadOnOutsideClick);
      };
    }
  }, [activeTool.activeTool, mobileDrawer?.isOpen, closeUploadTool]);

  // Force DOM to match Redux state for Upload Graphics tool (error-safe)
  useEffect(() => {
    try {
      if (
        currentTool &&
        (currentTool.id === "upload" || currentTool.id === "photos")
      ) {
        const tools = document.querySelector(".tools");
        const canvasContainer = document.querySelector(".canvas-container");

        // Only make DOM changes if the elements exist
        if (tools && canvasContainer) {
          // If Redux state says tool should be visible
          if (activeTool.isVisible && mobileDrawer?.isOpen) {
            tools.classList.remove("tools-hidden");
            canvasContainer.classList.add("tools-active");
          } else {
            tools.classList.add("tools-hidden");
            canvasContainer.classList.remove("tools-active");
          }
        }
      }
    } catch (error) {
      // Failed to update DOM from Redux state
    }
  }, [currentTool, activeTool.isVisible, mobileDrawer?.isOpen]);

  // Handle clicking arrow buttons to navigate
  const handleArrowClick = (direction) => {
    // Prevent executing if animation is in progress
    if (isAnimating) return;

    setIsAnimating(true);

    // Set the animation direction
    const animDir = direction === "next" ? "slide-left" : "slide-right";
    setAnimationDirection(animDir);

    // Calculate the new index
    const newIndex =
      direction === "next"
        ? (currentIndex + 1) % filteredTools.length
        : (currentIndex - 1 + filteredTools.length) % filteredTools.length;

    const newTool = filteredTools[newIndex];

    // Get the current drawer state to maintain it when switching tools
    const shouldKeepDrawerOpen = mobileDrawer?.isOpen || false;

    // Make the tool visible based on current drawer state
    selectToolCollection({
      activeTool: newTool.label,
      isVisible: shouldKeepDrawerOpen
    });

    // Ensure drawer state remains consistent
    if (shouldKeepDrawerOpen) {
      toggleMobileDrawer(true);
    }

    // Navigate to the new tool URL
    if (navigate) {
      navigate(`/${routeId}/customize/${newTool.id}${location.search || ""}`);
    }

    // Update the current index with animation
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsAnimating(false);
    }, 300);
  };

  const handleToolClick = (e, tool) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    // Find the index of the clicked tool
    const toolIndex = filteredTools.findIndex((t) => t.id === tool.id);
    if (toolIndex !== -1 && toolIndex !== currentIndex) {
      setCurrentIndex(toolIndex);
    }

    // Check if we're clicking on the same tool that's already active
    const isSameTool = activeTool.activeTool === tool.label;
    // Get reliable drawer state
    const drawerIsOpen = mobileDrawer?.isOpen || false;
    // Check if this is an upload tool (which was having issues)
    const isUploadTool = tool.id === "upload" || tool.id === "photos";

    if (isSameTool && drawerIsOpen) {
      // We're clicking on the currently active tool and the drawer is open,
      // so close the drawer and mark the tool as not visible
      selectToolCollection({ activeTool: tool.label, isVisible: false });
      toggleMobileDrawer(false);
    } else {
      // Either we're clicking on a different tool, or the drawer is closed
      selectToolCollection({ activeTool: tool.label, isVisible: true });
      toggleMobileDrawer(true);

      // Only navigate if we're changing tools
      if (navigate && !isSameTool) {
        navigate(`/${routeId}/customize/${tool.id}${location.search || ""}`);
      }
    }
  };

  const debugClickHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Force sync states by toggling drawer
    const newDrawerState = !(mobileDrawer?.isOpen || false);

    // Update both states
    toggleMobileDrawer(newDrawerState);
    selectToolCollection({
      activeTool: activeTool.activeTool,
      isVisible: newDrawerState
    });
  };

  // Helper function to handle Upload Graphics tool visibility with Redux only
  const directToggleUploadTool = (shouldBeVisible) => {
    // Determine the correct tool label
    const toolLabel = currentTool ? currentTool.label : "Upload Graphics";

    // Update Redux state only, let React handle the DOM
    selectToolCollection({ activeTool: toolLabel, isVisible: shouldBeVisible });
    toggleMobileDrawer(shouldBeVisible);
  };

  return (
    <div className="mobile-tool-carousel">
      {/* Special floating close button for Upload Graphics */}
      {isActive &&
        (currentTool.id === "upload" || currentTool.id === "photos") && (
          <button
            className="upload-close-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // This is specifically a close button, so always close
              selectToolCollection({
                activeTool: currentTool.label,
                isVisible: false
              });
              toggleMobileDrawer(false);

              // Direct DOM approach as a backup
              try {
                const tools = document.querySelector(".tools");
                const canvasContainer =
                  document.querySelector(".canvas-container");

                // Always close the drawer
                if (tools) tools.classList.add("tools-hidden");
                if (canvasContainer)
                  canvasContainer.classList.remove("tools-active");
              } catch (error) {
                // Failed to manipulate DOM
              }
            }}
          >
            Close
          </button>
        )}

      <div className={`carousel-item-container ${animationDirection}`}>
        <div
          className={`carousel-item ${isActive ? "active" : ""}`}
          data-tool-id={currentTool.id}
          onClickCapture={(e) => {
            // Special direct handler for Upload Graphics tool
            if (
              (currentTool.id === "upload" || currentTool.id === "photos") &&
              mobileDrawer?.isOpen
            ) {
              e.preventDefault();
              e.stopPropagation();

              // Always close the drawer when clicking the item directly
              selectToolCollection({
                activeTool: currentTool.label,
                isVisible: false
              });
              toggleMobileDrawer(false);

              // Direct DOM approach as a backup
              try {
                const tools = document.querySelector(".tools");
                const canvasContainer =
                  document.querySelector(".canvas-container");

                // Always close the drawer
                if (tools) tools.classList.add("tools-hidden");
                if (canvasContainer)
                  canvasContainer.classList.remove("tools-active");
              } catch (error) {
                // Failed to manipulate DOM
              }

              return false;
            }
          }}
        >
          <Link
            className="navigation__link"
            to={`/${routeId}/customize/${currentTool.id}${location.search || ""}`}
            onClick={(e) => {
              // Prevent default navigation and stop propagation
              e.preventDefault();
              e.stopPropagation();

              // Direct approach for Upload Graphics tool
              if (currentTool.id === "upload" || currentTool.id === "photos") {
                // Get current drawer state to determine if we should open or close
                const isDrawerOpen = mobileDrawer?.isOpen || false;

                // Toggle drawer based only on current state
                selectToolCollection({
                  activeTool: currentTool.label,
                  isVisible: !isDrawerOpen
                });
                toggleMobileDrawer(!isDrawerOpen);

                // Direct DOM approach as a backup
                try {
                  const tools = document.querySelector(".tools");
                  const canvasContainer =
                    document.querySelector(".canvas-container");

                  if (isDrawerOpen) {
                    // Close the drawer
                    if (tools) tools.classList.add("tools-hidden");
                    if (canvasContainer)
                      canvasContainer.classList.remove("tools-active");
                  } else {
                    // Open the drawer
                    if (tools) tools.classList.remove("tools-hidden");
                    if (canvasContainer)
                      canvasContainer.classList.add("tools-active");

                    // Navigate if needed when opening
                    if (navigate) {
                      navigate(
                        `/${routeId}/customize/${currentTool.id}${location.search || ""}`
                      );
                    }
                  }
                } catch (error) {
                  // Failed to manipulate DOM
                }
              } else {
                // For other tools, use normal handler
                handleToolClick(e, currentTool);
              }
            }}
          >
            {
              /* SVG background for Upload tool button */
              (currentTool.id === "upload" || currentTool.id === "photos") && (
                <UploadButton />
              )
            }
            <div className="tool-name-with-step">
              <span
                onClick={(e) => {
                  // Special handler for upload tool name click
                  if (
                    currentTool.id === "upload" ||
                    currentTool.id === "photos"
                  ) {
                    e.preventDefault();
                    e.stopPropagation();

                    // Get current drawer state to determine if we should open or close
                    const isDrawerOpen = mobileDrawer?.isOpen || false;

                    // Toggle drawer based only on current state
                    selectToolCollection({
                      activeTool: currentTool.label,
                      isVisible: !isDrawerOpen
                    });
                    toggleMobileDrawer(!isDrawerOpen);

                    // Direct DOM approach as a backup
                    try {
                      const tools = document.querySelector(".tools");
                      const canvasContainer =
                        document.querySelector(".canvas-container");

                      if (isDrawerOpen) {
                        // Close the drawer
                        if (tools) tools.classList.add("tools-hidden");
                        if (canvasContainer)
                          canvasContainer.classList.remove("tools-active");
                      } else {
                        // Open the drawer
                        if (tools) tools.classList.remove("tools-hidden");
                        if (canvasContainer)
                          canvasContainer.classList.add("tools-active");
                      }
                    } catch (error) {
                      // Failed to manipulate DOM
                    }
                  }
                }}
              >
                {currentTool.label}
              </span>
              <span className="step-indicator">
                {safeIndex + 1}/{filteredTools.length}
              </span>
            </div>
            {currentTool.id === "style" && (
              <div className="expand-indicator first-tool">
                <svg
                  className="caret"
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transform:
                      activeTool.activeTool === currentTool.label &&
                      (activeTool.isVisible || mobileDrawer?.isOpen)
                        ? "rotate(180deg)"
                        : "none"
                  }}
                >
                  <path
                    d="M1.41 0.590027L6 5.17003L10.59 0.590027L12 2.00003L6 8.00003L0 2.00003L1.41 0.590027Z"
                    fill="#A3D9CB"
                  />
                </svg>
              </div>
            )}
            {currentTool.id !== "style" && (
              <div
                className="caret-indicator"
                onClick={(e) => {
                  // Special handler for upload caret click
                  if (
                    currentTool.id === "upload" ||
                    currentTool.id === "photos"
                  ) {
                    e.preventDefault();
                    e.stopPropagation();

                    // Get current drawer state to determine if we should open or close
                    const isDrawerOpen = mobileDrawer?.isOpen || false;

                    // Toggle drawer based only on current state
                    selectToolCollection({
                      activeTool: currentTool.label,
                      isVisible: !isDrawerOpen
                    });
                    toggleMobileDrawer(!isDrawerOpen);

                    // Direct DOM approach as a backup
                    try {
                      const tools = document.querySelector(".tools");
                      const canvasContainer =
                        document.querySelector(".canvas-container");

                      if (isDrawerOpen) {
                        // Close the drawer
                        if (tools) tools.classList.add("tools-hidden");
                        if (canvasContainer)
                          canvasContainer.classList.remove("tools-active");
                      } else {
                        // Open the drawer
                        if (tools) tools.classList.remove("tools-hidden");
                        if (canvasContainer)
                          canvasContainer.classList.add("tools-active");
                      }
                    } catch (error) {
                      // Failed to manipulate DOM
                    }
                  }
                }}
              >
                {currentTool.id === "upload" || currentTool.id === "photos" ? (
                  // Special upload caret with simpler approach
                  <svg
                    className="caret upload-caret"
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform:
                        activeTool.activeTool === currentTool.label &&
                        (activeTool.isVisible || mobileDrawer?.isOpen)
                          ? "rotate(180deg)"
                          : "none"
                    }}
                  >
                    <path
                      className="upload-path"
                      d="M1.41 0.590027L6 5.17003L10.59 0.590027L12 2.00003L6 8.00003L0 2.00003L1.41 0.590027Z"
                      fill="#A3D9CB"
                    />
                  </svg>
                ) : (
                  // Regular caret for other tools
                  <svg
                    className="caret"
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform:
                        activeTool.activeTool === currentTool.label &&
                        (activeTool.isVisible || mobileDrawer?.isOpen)
                          ? "rotate(180deg)"
                          : "none"
                    }}
                  >
                    <path
                      d="M1.41 0.590027L6 5.17003L10.59 0.590027L12 2.00003L6 8.00003L0 2.00003L1.41 0.590027Z"
                      fill="#A3D9CB"
                    />
                  </svg>
                )}
              </div>
            )}
          </Link>
        </div>
      </div>

      <div className="carousel-navigation">
        {safeIndex > 0 && (
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleArrowClick("prev");
            }}
            aria-label="Previous tool"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
            >
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>
        )}

        {safeIndex < filteredTools.length - 1 && (
          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleArrowClick("next");
            }}
            aria-label="Next tool"
          >
            <svg
              className="caret"
              width="10"
              height="17"
              viewBox="0 0 10 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.052 15.316L8.01 8.358 1.052 1.4"
                strokeWidth="2"
                stroke="#acdaca"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Wrap with React Router's navigate
const MobileToolCarouselWithNavigate = (props) => {
  const navigate = useNavigate();
  return <MobileToolCarousel {...props} navigate={navigate} />;
};

// Map Redux state to props
const mapStateToProps = (state) => ({
  mobileDrawer: state.mobileDrawer
});

export default connect(mapStateToProps, (dispatch) =>
  bindActionCreators({ selectToolCollection, toggleMobileDrawer }, dispatch)
)(MobileToolCarouselWithNavigate);
